import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { User } from "@/types";

type UserRole = "learner" | "admin" | "controller";
type AssignableRole = "learner" | "controller";

const ROLE_LABELS: Record<UserRole, string> = {
  learner: "Driver",
  controller: "Controller",
  admin: "Admin",
};

const ROLE_BADGE: Record<UserRole, string> = {
  learner: "bg-blue-100 text-blue-800",
  controller: "bg-purple-100 text-purple-800",
  admin: "bg-red-100 text-red-800",
};

export default function AdminUsers() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const queryClient = useQueryClient();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin", "users", debouncedSearch],
    queryFn: () =>
      api.get<User[]>("/admin/users", {
        search: debouncedSearch || undefined,
        page_size: 50,
      }),
  });

  const updateRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: AssignableRole }) => {
      const url = `/admin/users/${userId}/role?role=${encodeURIComponent(role)}`;
      return api.put<User>(url);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (err: unknown) => {
      const detail = (err as { data?: { detail?: string } })?.data?.detail;
      alert(detail ?? "Failed to update role. Please try again.");
    },
  });

  function handleSearchChange(value: string) {
    setSearch(value);
    clearTimeout((window as any)._searchTimeout);
    (window as any)._searchTimeout = setTimeout(() => setDebouncedSearch(value), 400);
  }

  function handleRoleChange(user: User, role: AssignableRole) {
    if (role === user.role) return;
    updateRole.mutate({ userId: user.id, role });
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
          <p className="mt-1 text-sm text-gray-500">
            Search users and assign roles. All new users start as Drivers.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="w-full max-w-md rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-gray-500">Loading users...</div>
      ) : users.length === 0 ? (
        <div className="py-12 text-center text-gray-500">No users found.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Current Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Assign Role
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                    {user.first_name || user.last_name
                      ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                      : "—"}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {user.email}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${ROLE_BADGE[user.role as UserRole]}`}
                    >
                      {ROLE_LABELS[user.role as UserRole]}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <select
                      value={user.role === "admin" ? "admin" : user.role}
                      onChange={(e) => handleRoleChange(user, e.target.value as AssignableRole)}
                      disabled={updateRole.isPending || user.role === "admin"}
                      className="rounded border border-gray-300 px-3 py-1 text-sm focus:border-primary-500 focus:outline-none disabled:opacity-50"
                    >
                      {user.role === "admin" && (
                        <option value="admin" disabled>Admin</option>
                      )}
                      <option value="learner">Driver</option>
                      <option value="controller">Controller</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
